import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Container,
  Button,
  Flex,
  VStack,
  HStack,
  Text,
  useDisclosure,
  IconButton,
  Tooltip,
  useToast,
  Spinner,
  Center,
  Input,
  FormControl,
  FormLabel,
  Alert,
  AlertIcon,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalBody,
  ModalCloseButton,
  InputGroup,
  InputRightElement,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  MenuDivider,
  Checkbox,
  Link,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router-dom';
import { ChevronLeftIcon, ChevronRightIcon, ChevronUpIcon, ChevronDownIcon, AddIcon, MinusIcon, LinkIcon, ViewIcon, ViewOffIcon, LockIcon, SettingsIcon, ExternalLinkIcon } from '@chakra-ui/icons';
import { ColumnsIcon, RowsIcon } from '../components/KMDeck/GridControlIcons';
import ButtonGrid from '../components/KMDeck/ButtonGrid';
import ButtonEditor from '../components/KMDeck/ButtonEditor';
import PasswordSetupModal from '../components/PasswordSetupModal';
import KMDeckShareModal from '../components/KMDeckShareModal';
import ColorCustomizationModal from '../components/ColorCustomizationModal';
import TooltipKeyBox from '../components/TooltipKeyBox';
import { KMDeckConfig, KMDeckButton, GridSetupData, KMDeckTheme } from '../types/KMDeck';
import { KMDeckService } from '../services/KMDeckService';
import { isMobileDevice } from '../utils/deviceDetection';
// Removed old mapping functions - now using simple R1C1 coordinate system
import { setupAddressBarHiding } from '../utils/addressBarManager';
import { UrlCleaner } from '../utils/urlCleaner';


// Blocked remote trigger unique ID that should show an alert instead of allowing setup
const BLOCKED_REMOTE_TRIGGER_ID = 'E0437FD9-F103-4622-B64B-6F6D21B47883/5DDC5BF3-194B-437C-9C2F-A2736EAAD459';

const KMDeck: React.FC = () => {
  const navigate = useNavigate();
  const toast = useToast();
  
  // State
  const [config, setConfig] = useState<KMDeckConfig | null>(null);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [editingButton, setEditingButton] = useState<KMDeckButton | null>(null);
  const [editingPosition, setEditingPosition] = useState<string>('R1C1');
  const [isLoading, setIsLoading] = useState(false);
  const [remoteTriggerUrl, setRemoteTriggerUrl] = useState<string>('');
  const [faviconUrl, setFaviconUrl] = useState<string>('');
  const [showSetup, setShowSetup] = useState<boolean>(false);
  const [availableHeight, setAvailableHeight] = useState<number | undefined>(undefined);
  const [isLandscapeMode, setIsLandscapeMode] = useState<boolean>(false);
  const [hideButtonLabels, setHideButtonLabels] = useState<boolean>(true);
  
  // Touch/swipe state for mobile page navigation
  const [touchStartX, setTouchStartX] = useState<number>(0);
  const [touchEndX, setTouchEndX] = useState<number>(0);
  const [isTransitioning, setIsTransitioning] = useState<boolean>(false);
  
  // Glow effect state
  const [glowingButtons, setGlowingButtons] = useState<Set<string>>(new Set());
  const glowTimeouts = useRef<Map<string, NodeJS.Timeout>>(new Map());
  
  // Modals
  const { isOpen: isButtonEditorOpen, onOpen: onButtonEditorOpen, onClose: onButtonEditorClose } = useDisclosure();
  const { isOpen: isPasswordSetupOpen, onOpen: onPasswordSetupOpen, onClose: onPasswordSetupClose } = useDisclosure();
  const { isOpen: isShareModalOpen, onOpen: onShareModalOpen, onClose: onShareModalClose } = useDisclosure();
  const { isOpen: isSplitModalOpen, onOpen: onSplitModalOpen, onClose: onSplitModalClose } = useDisclosure();
  const { isOpen: isUnsplitModalOpen, onOpen: onUnsplitModalOpen, onClose: onUnsplitModalClose } = useDisclosure();
  const { isOpen: isBlockedMacroAlertOpen, onOpen: onBlockedMacroAlertOpen, onClose: onBlockedMacroAlertClose } = useDisclosure();
  const { isOpen: isColorModalOpen, onOpen: onColorModalOpen, onClose: onColorModalClose } = useDisclosure();
  const { isOpen: isWhatsKMDeckOpen, onOpen: onWhatsKMDeckOpen, onClose: onWhatsKMDeckClose } = useDisclosure();
  
  // Password protection state
  const [isPasswordRequired, setIsPasswordRequired] = useState(false);
  const [password, setPassword] = useState('');
  const [passwordError, setPasswordError] = useState<string | null>(null);
  const [isPasswordLoading, setIsPasswordLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [resetLandscapeButtons, setResetLandscapeButtons] = useState<boolean>(false);
  const [retainView, setRetainView] = useState<'portrait' | 'landscape'>('portrait');
  
  // File input ref for import
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  
  // Drag and drop state
  const [isDragOver, setIsDragOver] = useState(false);

  // Check for blocked macro after deck loads
  useEffect(() => {
    if (config && remoteTriggerUrl) {
      const triggerId = remoteTriggerUrl.startsWith('https://trigger.keyboardmaestro.com/t/') 
        ? remoteTriggerUrl.replace('https://trigger.keyboardmaestro.com/t/', '')
        : remoteTriggerUrl;
      
      if (triggerId === BLOCKED_REMOTE_TRIGGER_ID || triggerId.includes(BLOCKED_REMOTE_TRIGGER_ID)) {
        // Clear the blocked URL and show alert
        setRemoteTriggerUrl('');
        onBlockedMacroAlertOpen();
      }
    }
  }, [config, remoteTriggerUrl, onBlockedMacroAlertOpen]);



  // Calculate available height for desktop layout
  useEffect(() => {
    let resizeTimeout: NodeJS.Timeout;
    let lastWidth = window.innerWidth;
    let lastHeight = window.innerHeight;
    
    const calculateAvailableHeight = () => {
      if (!isMobileDevice()) {
        // For desktop, calculate available height by subtracting header and other UI elements
        const viewportHeight = window.innerHeight;
        
        // Estimate header height (navigation + padding)
        const headerHeight = 80; // Approximate height of header section
        
        // Estimate page management section height
        const pageManagementHeight = 60;
        
        // Estimate status info height
        const statusInfoHeight = 40;
        
        // Calculate available height
        const calculatedHeight = viewportHeight - headerHeight - pageManagementHeight - statusInfoHeight;
        setAvailableHeight(Math.max(calculatedHeight, 300)); // Minimum height of 300px
      } else {
        // For mobile, use full viewport height
        setAvailableHeight(undefined);
      }
    };

    const handleResize = () => {
      const currentWidth = window.innerWidth;
      const currentHeight = window.innerHeight;
      
      // Only proceed if dimensions actually changed
      if (currentWidth !== lastWidth || currentHeight !== lastHeight) {
        lastWidth = currentWidth;
        lastHeight = currentHeight;
        
        // Clear any existing timeout
        clearTimeout(resizeTimeout);
        
        // Wait for window to be stable for 300ms before recalculating
        resizeTimeout = setTimeout(() => {
          calculateAvailableHeight();
        }, 300);
      }
    };

    calculateAvailableHeight();
    
    // Recalculate on window resize with stable size detection
    window.addEventListener('resize', handleResize);
    
    return () => {
      clearTimeout(resizeTimeout);
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  // Add KMDeck class to body for CSS overrides and setup address bar hiding
  useEffect(() => {
    document.body.classList.add('kmdeck-page');
    
    // Completely remove rotation message for KMDeck
    const rotationMessage = document.querySelector('.rotation-message');
    if (rotationMessage) {
      rotationMessage.remove();
    }
    
    // On mobile, detect device orientation and update isLandscapeMode accordingly
    if (isMobileDevice()) {
      const checkMobileOrientation = () => {
        const width = window.innerWidth;
        const height = window.innerHeight;
        const deviceIsLandscape = width > height && (
          height < width * 0.8 || height < 500
        );
        setIsLandscapeMode(deviceIsLandscape);
      };
      
      // Check initial orientation
      checkMobileOrientation();
      
      // Listen for orientation changes
      const handleOrientationChange = () => {
        // Use multiple timeouts to catch orientation changes at different stages
        setTimeout(checkMobileOrientation, 50);
        setTimeout(checkMobileOrientation, 300);
        setTimeout(checkMobileOrientation, 600);
      };
      
      window.addEventListener('orientationchange', handleOrientationChange);
      window.addEventListener('resize', handleOrientationChange);
      
      return () => {
        window.removeEventListener('orientationchange', handleOrientationChange);
        window.removeEventListener('resize', handleOrientationChange);
      };
    }
    
    // Set the page title for KMDeck
    document.title = 'KMDeck - Mobile Keyboard Maestro Triggers';
    
    // Update meta description for social media previews
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', 'Mobile Keyboard Maestro triggers for remote macro execution');
    }
    
    // Check for query parameters - URLs with data parameters should not be indexed
    const searchParams = new URLSearchParams(window.location.search);
    const hasDataParam = searchParams.has('data') || searchParams.has('encrypted');
    
    let robotsTag = document.querySelector('meta[name="robots"]') as HTMLMetaElement;
    if (!robotsTag) {
      robotsTag = document.createElement('meta');
      robotsTag.setAttribute('name', 'robots');
      document.head.appendChild(robotsTag);
    }
    
    // If URL has data parameter, set noindex (shared URLs should not be indexed)
    if (hasDataParam) {
      robotsTag.setAttribute('content', 'noindex, nofollow');
      
      // Set canonical to clean URL without query params
      let canonicalLink = document.querySelector('link[rel="canonical"]') as HTMLLinkElement;
      if (!canonicalLink) {
        canonicalLink = document.createElement('link');
        canonicalLink.setAttribute('rel', 'canonical');
        document.head.appendChild(canonicalLink);
      }
      canonicalLink.setAttribute('href', 'https://abmymix.com/KMDeck');
    } else {
      // Ensure KMDeck page is indexed (without query parameters)
      robotsTag.setAttribute('content', 'index, follow');
    }
    
    // Add Open Graph meta tags for better social media previews
    const addOrUpdateMetaTag = (property: string, content: string) => {
      let metaTag = document.querySelector(`meta[property="${property}"]`) as HTMLMetaElement;
      if (!metaTag) {
        metaTag = document.createElement('meta');
        metaTag.setAttribute('property', property);
        document.head.appendChild(metaTag);
      }
      metaTag.setAttribute('content', content);
    };
    
    addOrUpdateMetaTag('og:title', 'KMDeck - Mobile Keyboard Maestro Triggers');
    addOrUpdateMetaTag('og:description', 'Mobile Keyboard Maestro triggers for remote macro execution');
    addOrUpdateMetaTag('og:type', 'website');
    
    // Set KM Deck specific favicon and Apple touch icon
    const setFavicon = (href: string) => {
      // Update regular favicon
      let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
      if (!favicon) {
        favicon = document.createElement('link');
        favicon.setAttribute('rel', 'icon');
        favicon.setAttribute('type', 'image/png');
        document.head.appendChild(favicon);
      }
      favicon.setAttribute('href', href);
      
      // Update Apple touch icon
      let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
      if (!appleTouchIcon) {
        appleTouchIcon = document.createElement('link');
        appleTouchIcon.setAttribute('rel', 'apple-touch-icon');
        document.head.appendChild(appleTouchIcon);
      }
      appleTouchIcon.setAttribute('href', href);
      
      // Update Apple touch icon with size
      let appleTouchIconSized = document.querySelector('link[rel="apple-touch-icon"][sizes]') as HTMLLinkElement;
      if (!appleTouchIconSized) {
        appleTouchIconSized = document.createElement('link');
        appleTouchIconSized.setAttribute('rel', 'apple-touch-icon');
        appleTouchIconSized.setAttribute('sizes', '180x180');
        document.head.appendChild(appleTouchIconSized);
      }
      appleTouchIconSized.setAttribute('href', href);
    };
    
    // Use custom favicon if available, otherwise use default
    const faviconToUse = config?.faviconUrl || '/kmdeck-favicon.png?v=1';
    setFavicon(faviconToUse);
    
    // Setup address bar hiding for mobile landscape mode
    const cleanupAddressBarHiding = setupAddressBarHiding();
    
    return () => {
      document.body.classList.remove('kmdeck-page');
      // Reset to default title when leaving KMDeck
      document.title = 'ABmyMix - Audio Comparison Tool';
      
      // Reset meta description
      if (metaDescription) {
        metaDescription.setAttribute('content', 'A modern audio comparison tool for A/B testing audio files');
      }
      
      // Remove Open Graph tags
      const ogTags = document.querySelectorAll('meta[property^="og:"]');
      ogTags.forEach(tag => tag.remove());
      
      // Reset favicon to default
      setFavicon('/favicon.png?v=2');
      
      // Cleanup glow timeouts
      glowTimeouts.current.forEach(timeout => clearTimeout(timeout));
      glowTimeouts.current.clear();
      
      cleanupAddressBarHiding();
    };
  }, []);


  // Load shared configuration on mount
  useEffect(() => {
    const loadSharedConfig = async () => {
      const isPasswordProtected = KMDeckService.isPasswordProtectedUrl();
      
      if (isPasswordProtected) {
        // Show inline password form for encrypted URLs
        setIsPasswordRequired(true);
        return;
      }
      
      const shareData = KMDeckService.getShareDataFromUrl();
      if (shareData) {
        setIsLoading(true);
        try {
          const decompressed = KMDeckService.decompressShareData(shareData);
          if (decompressed) {
            const configToUse = decompressed.config;
            
            setRemoteTriggerUrl(configToUse.remoteTriggerUrl);
            
            // Load the favicon URL from shared config
            if (decompressed.config.faviconUrl) {
              setFaviconUrl(decompressed.config.faviconUrl);
            }
            
            setConfig(configToUse);
            console.log('[KMDeck] Loaded shared configuration:', configToUse);
            
            // Clean the URL to hide share data parameters
            UrlCleaner.cleanUrl();
            
          } else {
            console.error('[KMDeck] Failed to decompress share data');
            toast({
              title: 'Invalid share data',
              description: 'The shared configuration could not be loaded',
              status: 'error',
              duration: 5000,
              isClosable: true
            });
          }
        } catch (error) {
          console.error('[KMDeck] Error loading shared config:', error);
          toast({
            title: 'Error loading configuration',
            description: 'Please try again',
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        } finally {
          setIsLoading(false);
        }
      } else {
        // No shared config, create a default grid
        console.log('[KMDeck] No shared config found, creating default grid');
        const defaultConfig = KMDeckService.createNewConfig(5, 3, '', false, '');
        setConfig(defaultConfig);
      }
    };

    loadSharedConfig();
  }, [toast]);

  // Update favicon when config changes
  useEffect(() => {
    if (config?.faviconUrl) {
      const setFavicon = (href: string) => {
        // Update regular favicon
        let favicon = document.querySelector('link[rel="icon"]') as HTMLLinkElement;
        if (!favicon) {
          favicon = document.createElement('link');
          favicon.setAttribute('rel', 'icon');
          favicon.setAttribute('type', 'image/png');
          document.head.appendChild(favicon);
        }
        favicon.setAttribute('href', href);
        
        // Update Apple touch icon
        let appleTouchIcon = document.querySelector('link[rel="apple-touch-icon"]') as HTMLLinkElement;
        if (!appleTouchIcon) {
          appleTouchIcon = document.createElement('link');
          appleTouchIcon.setAttribute('rel', 'apple-touch-icon');
          document.head.appendChild(appleTouchIcon);
        }
        appleTouchIcon.setAttribute('href', href);
        
        // Update Apple touch icon with size
        let appleTouchIconSized = document.querySelector('link[rel="apple-touch-icon"][sizes]') as HTMLLinkElement;
        if (!appleTouchIconSized) {
          appleTouchIconSized = document.createElement('link');
          appleTouchIconSized.setAttribute('rel', 'apple-touch-icon');
          appleTouchIconSized.setAttribute('sizes', '180x180');
          document.head.appendChild(appleTouchIconSized);
        }
        appleTouchIconSized.setAttribute('href', href);
      };
      
      setFavicon(config.faviconUrl);
    }
  }, [config?.faviconUrl]);


  // Debounce map to prevent double-triggering (especially in React StrictMode)
  // Note: React StrictMode intentionally double-invokes functions in development mode
  // This causes double triggering in dev, but works correctly in production
  const lastTriggerTime = useRef<Map<string, number>>(new Map());
  const lastToggleClickTime = useRef<Map<string, number>>(new Map());
  const processingToggle = useRef<Map<string, boolean>>(new Map());
  const lastButtonClick = useRef<Map<string, number>>(new Map());
  const activeClicks = useRef<Set<string>>(new Set());
  const TOGGLE_DEBOUNCE_MS = 500; // 500ms debounce for toggle buttons
  const BUTTON_CLICK_DEBOUNCE_MS = 100; // 100ms debounce for any button click

  // Function to trigger glow effect for a button
  const triggerGlowEffect = (buttonId: string) => {
    console.log('[KMDeck] Triggering glow effect for button:', buttonId);
    
    // Clear any existing timeout for this button
    const existingTimeout = glowTimeouts.current.get(buttonId);
    if (existingTimeout) {
      clearTimeout(existingTimeout);
    }
    
    // Add button to glowing set
    setGlowingButtons(prev => {
      const newSet = new Set(prev).add(buttonId);
      console.log('[KMDeck] Button added to glowing set:', buttonId, 'Current glowing buttons:', Array.from(newSet));
      return newSet;
    });
    
    // Set timeout to remove glow after 0.5 seconds
    const timeout = setTimeout(() => {
      console.log('[KMDeck] Removing glow effect for button:', buttonId);
      setGlowingButtons(prev => {
        const newSet = new Set(prev);
        newSet.delete(buttonId);
        console.log('[KMDeck] Button removed from glowing set:', buttonId, 'Current glowing buttons:', Array.from(newSet));
        return newSet;
      });
      glowTimeouts.current.delete(buttonId);
    }, 500);
    
    glowTimeouts.current.set(buttonId, timeout);
  };

  const handleButtonClick = async (button: KMDeckButton) => {
    if (!config) return;

    // Trigger glow effect immediately when button is clicked
    triggerGlowEffect(button.id);

    // Check for rapid button clicks first (before any other processing)
    const now = Date.now();
    const lastClick = lastButtonClick.current.get(button.id);
    
    if (lastClick && (now - lastClick) < BUTTON_CLICK_DEBOUNCE_MS) {
      console.log('[KMDeck] Ignoring rapid button click (debounced):', button.id, 'Time since last click:', now - lastClick, 'ms');
      return;
    }
    
    // Check if this button is already being processed
    if (activeClicks.current.has(button.id)) {
      console.log('[KMDeck] Button already being processed, ignoring:', button.id);
      return;
    }
    
    // Mark this button as being processed
    activeClicks.current.add(button.id);
    
    // Update the last button click time immediately
    lastButtonClick.current.set(button.id, now);

    try {
      // Handle toggle buttons
      if (button.isToggle === true && button.toggleState1 && button.toggleState2) {
        // Check if this toggle is already being processed
        if (processingToggle.current.get(button.id)) {
          console.log('[KMDeck] Toggle already being processed, ignoring (React StrictMode in dev):', button.id);
          return;
        }
        
        // Check for rapid double-clicks on toggle buttons
        const now = Date.now();
        const lastToggleClick = lastToggleClickTime.current.get(button.id);
        
        if (lastToggleClick && (now - lastToggleClick) < TOGGLE_DEBOUNCE_MS) {
          console.log('[KMDeck] Ignoring rapid toggle click (debounced):', button.id, 'Time since last click:', now - lastToggleClick, 'ms');
          return;
        }
        
        // Set processing flag and update the last toggle click time IMMEDIATELY
        processingToggle.current.set(button.id, true);
        lastToggleClickTime.current.set(button.id, now);
        console.log('[KMDeck] Processing toggle click for:', button.id, 'at', now);
        
        // Ensure toggleState is properly initialized (default to false for state 1)
        const currentToggleState = button.toggleState ?? false;
        
        // Get the current state (what we're about to trigger)
        const currentState = currentToggleState ? button.toggleState2 : button.toggleState1;
        
        console.log('[KMDeck] Toggle button clicked:', {
          buttonId: button.id,
          currentToggleState,
          currentState: currentToggleState ? 'State 2' : 'State 1',
          currentStateMacro: currentState.macroUuid,
          currentStateTitle: currentState.title
        });
        
        // Check if current state has a macro configured
        if (!currentState.macroUuid) {
          toast({
            title: 'Button not configured',
            description: `State ${currentToggleState ? '2' : '1'} needs a macro UUID to be configured first`,
            status: 'warning',
            duration: 3000,
            isClosable: true
          });
          return;
        }
        
        // Trigger the macro with the CURRENT state
        console.log('[KMDeck] Triggering macro for current state:', currentState.macroUuid);
        await triggerMacro(currentState);
        
        // Toggle the state for the next click
        const newToggleState = !currentToggleState;
        const nextState = newToggleState ? button.toggleState2 : button.toggleState1;
        
        console.log('[KMDeck] Toggling to next state:', {
          newToggleState,
          nextState: newToggleState ? 'State 2' : 'State 1',
          nextStateTitle: nextState.title
        });
        
        // Update the button's toggle state in the config
        const updatedConfig = { ...config };
        const updatedPages = [...updatedConfig.pages];
        
        // Determine which page to update (same logic as ButtonGrid)
        let targetPage;
        const currentPage = updatedPages[currentPageIndex];
        if (currentPage.isSplitPortraitLandscape && isLandscapeMode) {
          targetPage = getLandscapePage(currentPage);
        } else {
          targetPage = currentPage;
        }
        
        if (!targetPage) {
          console.error('[KMDeck] Target page not found');
          return;
        }
        
        // Find the target page index in the updated pages array
        const targetPageIndex = updatedPages.findIndex(p => p.id === targetPage.id);
        if (targetPageIndex === -1) {
          console.error('[KMDeck] Target page not found in pages array:', targetPage.id);
          return;
        }
        
        const updatedTargetPage = { ...targetPage };
        const updatedButtons = [...updatedTargetPage.buttons];
        
        const buttonIndex = updatedButtons.findIndex(b => b.id === button.id);
        
        if (buttonIndex >= 0) {
          const updatedButton = {
            ...button,
            toggleState: newToggleState,
            // Update the main button properties to reflect the NEXT state (for display)
            title: nextState.title,
            backgroundColor: nextState.backgroundColor,
            backgroundImageUrl: nextState.backgroundImageUrl,
            macroUuid: nextState.macroUuid,
            parameter: nextState.parameter,
            fontFamily: nextState.fontFamily,
            fontSize: nextState.fontSize,
            fontColor: nextState.fontColor,
            hideTitle: nextState.hideTitle
          };
          
          updatedButtons[buttonIndex] = updatedButton;
          updatedTargetPage.buttons = updatedButtons;
          updatedPages[targetPageIndex] = updatedTargetPage;
          updatedConfig.pages = updatedPages;
          
          console.log('[KMDeck] Updated button:', {
            buttonId: updatedButton.id,
            newToggleState,
            newTitle: updatedButton.title,
            newBackgroundColor: updatedButton.backgroundColor,
            newMacroUuid: updatedButton.macroUuid,
            targetPageId: updatedTargetPage.id,
            isLandscapeMode
          });
          
          setConfig(updatedConfig);
        } else {
          console.error('[KMDeck] Button not found in target page:', button.id, 'targetPageId:', updatedTargetPage.id);
        }
        
        // Clear the processing flags
        processingToggle.current.set(button.id, false);
        activeClicks.current.delete(button.id);
        console.log('[KMDeck] Toggle processing completed for:', button.id);
        
        return;
      }

      // Handle regular buttons
      if (!button.macroUuid) {
        toast({
          title: 'Button not configured',
          description: 'This button needs a macro UUID to be configured first',
          status: 'warning',
          duration: 3000,
          isClosable: true
        });
        return;
      }

      await triggerMacro(button);
      
      // Clear the active click flag for regular buttons
      activeClicks.current.delete(button.id);
      console.log('[KMDeck] Regular button processing completed for:', button.id);
    } catch (error) {
      console.error('[KMDeck] Error handling button click:', error);
      
      // Clear processing flags in case of error
      if (button.isToggle) {
        processingToggle.current.set(button.id, false);
        console.log('[KMDeck] Cleared toggle processing flag due to error for:', button.id);
      }
      activeClicks.current.delete(button.id);
      console.log('[KMDeck] Cleared active click flag due to error for:', button.id);
      
      toast({
        title: 'Button error',
        description: 'An error occurred while processing the button click',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    }
  };

  const triggerMacro = async (buttonState: { macroUuid: string; parameter?: string }) => {
    const triggerId = `${buttonState.macroUuid}-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    console.log('[KMDeck] triggerMacro called with:', { ...buttonState, triggerId, timestamp: Date.now() });
    
    // Use the remote trigger URL from the config
    const effectiveUrl = config?.remoteTriggerUrl || remoteTriggerUrl;

    if (!effectiveUrl) {
      console.log('[KMDeck] No remote trigger URL configured');
      toast({
        title: 'Setup required',
        description: 'Please configure the remote trigger URL in setup',
        status: 'warning',
        duration: 3000,
        isClosable: true
      });
      return;
    }
    
    console.log('[KMDeck] Using remote trigger URL:', effectiveUrl);

    // Create a unique key for this button's trigger
    // Include a timestamp to make each trigger unique for toggle buttons
    const now = Date.now();
    
    // For toggle buttons, we want to allow rapid toggling, so we'll use a different debounce strategy
    // Check if this exact macro+parameter combination was triggered very recently
    const baseTriggerKey = `${buttonState.macroUuid}-${buttonState.parameter || ''}`;
    const lastTrigger = lastTriggerTime.current.get(baseTriggerKey);

    // Check if this trigger was called recently (debounce) - reduced from 500ms to 150ms for better responsiveness                                                                                                    
    if (lastTrigger && (now - lastTrigger) < 150) {
      console.log('[KMDeck] Ignoring duplicate trigger (debounced):', baseTriggerKey, 'Time since last trigger:', now - lastTrigger, 'ms');
      return;
    }

    // Update the last trigger time
    lastTriggerTime.current.set(baseTriggerKey, now);

    // Create a minimal button object for the trigger
    const triggerButton: KMDeckButton = {
      id: 'trigger-button',
      title: '',
      backgroundColor: '#1a1a1a',
      macroUuid: buttonState.macroUuid,
      parameter: buttonState.parameter
    };

    const url = KMDeckService.generateKeyboardMaestroUrl(triggerButton, effectiveUrl);
    console.log('[KMDeck] Generated URL:', url);
    
    if (url) {
      try {
        // Use fetch() for fast, reliable Keyboard Maestro triggers
        fetch(url, { 
          method: 'GET',
          mode: 'no-cors',
          cache: 'no-cache'
        }).then(() => {
          console.log('[KMDeck] Trigger sent successfully');
        }).catch(error => {
          console.error('[KMDeck] Trigger failed:', error);
          toast({
            title: 'Trigger failed',
            description: 'Could not trigger the remote macro. Check console for details.',
            status: 'error',
            duration: 5000,
            isClosable: true
          });
        });
        
      } catch (error) {
        console.error('[KMDeck] Failed to trigger macro:', error);
        console.error('[KMDeck] URL attempted:', url);
        toast({
          title: 'Trigger failed',
          description: 'Could not trigger the remote macro. Check console for details.',
          status: 'error',
          duration: 5000,
          isClosable: true
        });
      }
    } else {
      console.log('[KMDeck] Failed to generate URL - invalid configuration');
      toast({
        title: 'Invalid configuration',
        description: 'Button configuration is invalid',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const handleButtonEdit = (button: KMDeckButton | null, r1c1Position: string) => {
    setEditingButton(button);
    setEditingPosition(r1c1Position);
    onButtonEditorOpen();
  };

  const handleButtonSwap = (fromR1C1: string, toR1C1: string) => {
    if (!config) return;

    const updatedConfig = { ...config };
    
    // Determine which page to work with (same logic as ButtonGrid)
    let targetPage;
    const currentPage = updatedConfig.pages[currentPageIndex];
    if (currentPage.isSplitPortraitLandscape && isLandscapeMode) {
      targetPage = getLandscapePage(currentPage);
    } else {
      targetPage = currentPage;
    }
    
    // Find buttons by their R1C1 positions
    const fromButton = targetPage.buttons.find((b: KMDeckButton) => {
      const parts = b.id.split('-');
      const buttonR1C1 = parts[parts.length - 1];
      return buttonR1C1 === fromR1C1;
    });
    
    const toButton = targetPage.buttons.find((b: KMDeckButton) => {
      const parts = b.id.split('-');
      const buttonR1C1 = parts[parts.length - 1];
      return buttonR1C1 === toR1C1;
    });
    
    if (fromButton && toButton) {
      // Swap the button IDs
      fromButton.id = `button-${targetPage.id}-${toR1C1}`;
      toButton.id = `button-${targetPage.id}-${fromR1C1}`;
      
      setConfig(updatedConfig);
      console.log('[KMDeck] Buttons swapped on page:', targetPage.id);
    } else if (fromButton && !toButton) {
      // Moving a button to an empty position
      fromButton.id = `button-${targetPage.id}-${toR1C1}`;
      
      setConfig(updatedConfig);
      console.log('[KMDeck] Button moved on page:', targetPage.id);
    }
  };

  const handleButtonCopy = (fromR1C1: string, toR1C1: string) => {
    if (!config) return;

    const updatedConfig = { ...config };
    
    // Determine which page to work with (same logic as ButtonGrid)
    let targetPage;
    const currentPage = updatedConfig.pages[currentPageIndex];
    if (currentPage.isSplitPortraitLandscape && isLandscapeMode) {
      targetPage = getLandscapePage(currentPage);
    } else {
      targetPage = currentPage;
    }
    
    // Find the source button
    const fromButton = targetPage.buttons.find((b: KMDeckButton) => {
      const parts = b.id.split('-');
      const buttonR1C1 = parts[parts.length - 1];
      return buttonR1C1 === fromR1C1;
    });
    
    if (!fromButton) return;
    
    // Check if there's already a button at the destination
    const existingToButton = targetPage.buttons.find((b: KMDeckButton) => {
      const parts = b.id.split('-');
      const buttonR1C1 = parts[parts.length - 1];
      return buttonR1C1 === toR1C1;
    });
    
    if (existingToButton) {
      // Copy settings to existing button
      existingToButton.title = fromButton.title;
      existingToButton.backgroundColor = fromButton.backgroundColor;
      existingToButton.backgroundImageUrl = fromButton.backgroundImageUrl;
      existingToButton.macroUuid = fromButton.macroUuid;
      existingToButton.parameter = fromButton.parameter;
      existingToButton.fontFamily = fromButton.fontFamily;
      existingToButton.fontSize = fromButton.fontSize;
      existingToButton.fontColor = fromButton.fontColor;
      existingToButton.hideTitle = fromButton.hideTitle;
      existingToButton.isToggle = fromButton.isToggle;
      existingToButton.toggleState1 = fromButton.toggleState1;
      existingToButton.toggleState2 = fromButton.toggleState2;
      
      console.log('[KMDeck] Button settings copied on page:', targetPage.id);
    } else {
      // Create a new button with copied settings
      const newButton: KMDeckButton = {
        id: `button-${targetPage.id}-${toR1C1}`,
        title: fromButton.title,
        backgroundColor: fromButton.backgroundColor,
        backgroundImageUrl: fromButton.backgroundImageUrl,
        macroUuid: fromButton.macroUuid,
        parameter: fromButton.parameter,
        fontFamily: fromButton.fontFamily,
        fontSize: fromButton.fontSize,
        fontColor: fromButton.fontColor,
        hideTitle: fromButton.hideTitle,
        isToggle: fromButton.isToggle,
        toggleState1: fromButton.toggleState1,
        toggleState2: fromButton.toggleState2
      };
      
      targetPage.buttons.push(newButton);
      console.log('[KMDeck] New button created on page:', targetPage.id);
    }
    
    setConfig(updatedConfig);
  };

  const handleButtonSave = (button: KMDeckButton) => {
    if (!config) return;

    const updatedConfig = { ...config };
    
    // Determine which page to save to
    let targetPage;
    const currentPage = updatedConfig.pages[currentPageIndex];
    if (currentPage.isSplitPortraitLandscape && isLandscapeMode) {
      // Save to landscape page
      targetPage = getLandscapePage(currentPage);
    } else {
      // Save to current page
      targetPage = currentPage;
    }
    
    // Find existing button at this R1C1 position
    const existingButtonIndex = targetPage.buttons.findIndex((b: KMDeckButton) => {
      const parts = b.id.split('-');
      const buttonR1C1 = parts[parts.length - 1];
      return buttonR1C1 === editingPosition;
    });
    
    if (existingButtonIndex >= 0) {
      // Update existing button
      const updatedButton = {
        ...targetPage.buttons[existingButtonIndex],
        ...button,
        id: `button-${targetPage.id}-${editingPosition}`
      };
      targetPage.buttons[existingButtonIndex] = updatedButton;
    } else {
      // Create new button
      const newButton = {
        ...button,
        id: `button-${targetPage.id}-${editingPosition}`
      };
      targetPage.buttons.push(newButton);
    }
    
    setConfig(updatedConfig);
    console.log('[KMDeck] Button saved to page:', targetPage.id, 'position:', editingPosition);
  };

  // Update config when remote trigger URL changes
  const updateConfigConnection = (newUrl: string) => {
    if (config) {
      const updatedConfig = { ...config, remoteTriggerUrl: newUrl };
      setConfig(updatedConfig);
    }
  };

  // Update config when favicon URL changes
  const updateConfigFavicon = (newFaviconUrl: string) => {
    if (config) {
      const updatedConfig = { ...config, faviconUrl: newFaviconUrl };
      setConfig(updatedConfig);
    }
  };



  const handleAddPage = () => {
    console.log('[KMDeck.handleAddPage] Starting handleAddPage with config:', {
      totalPages: config?.pages.length,
      pageIds: config?.pages.map(p => p.id),
      currentPageIndex
    });
    
    if (!config || config.pages.length >= 3) {
      console.log('[KMDeck.handleAddPage] Cannot add page - limit reached or no config');
      return;
    }
    
    const updatedConfig = KMDeckService.addPage(config);
    console.log('[KMDeck.handleAddPage] After addPage:', {
      totalPages: updatedConfig.pages.length,
      pageIds: updatedConfig.pages.map(p => p.id),
      newPageIndex: updatedConfig.pages.length - 1
    });
    
    setConfig(updatedConfig);
    
    // Navigate to the last main page (the one we just created)
    const mainPages = updatedConfig.pages.filter(page => !page.id.endsWith('-landscape'));
    const lastMainPage = mainPages[mainPages.length - 1];
    const newPageIndex = updatedConfig.pages.findIndex(page => page.id === lastMainPage.id);
    
    setCurrentPageIndex(newPageIndex);
    console.log('[KMDeck] Page added - new page index:', newPageIndex, 'page number:', newPageIndex + 1, 'pageId:', lastMainPage.id);
  };

  const handleRemovePage = () => {
    if (!config || config.pages.length <= 1) return;
    
    const updatedConfig = KMDeckService.removePage(config, config.pages[currentPageIndex].id);
    setConfig(updatedConfig);
    setCurrentPageIndex(Math.min(currentPageIndex, updatedConfig.pages.length - 1));
    console.log('[KMDeck] Page removed');
  };

  const handleAddRow = () => {
    if (!config || config.rows >= 8) return;
    
    const updatedConfig = KMDeckService.addRow(config);
    setConfig(updatedConfig);
    console.log('[KMDeck] Row added');
  };

  const handleRemoveRow = () => {
    if (!config || config.rows <= 1) return;
    
    const updatedConfig = KMDeckService.removeRow(config);
    setConfig(updatedConfig);
    console.log('[KMDeck] Row removed');
  };

  const handleAddColumn = () => {
    if (!config || config.columns >= 8) return;
    
    const updatedConfig = KMDeckService.addColumn(config);
    setConfig(updatedConfig);
    console.log('[KMDeck] Column added');
  };

  const handleRemoveColumn = () => {
    if (!config || config.columns <= 1) return;
    
    const updatedConfig = KMDeckService.removeColumn(config);
    setConfig(updatedConfig);
    console.log('[KMDeck] Column removed');
  };

  const handleRowsChange = (valueString: string, valueNumber: number) => {
    if (!config || isNaN(valueNumber) || valueNumber < 1 || valueNumber > 8) return;
    
    if (valueNumber > config.rows) {
      // Adding rows
      let updatedConfig = config;
      for (let i = config.rows; i < valueNumber; i++) {
        updatedConfig = KMDeckService.addRow(updatedConfig);
      }
      setConfig(updatedConfig);
    } else if (valueNumber < config.rows) {
      // Removing rows
      let updatedConfig = config;
      for (let i = config.rows; i > valueNumber; i--) {
        updatedConfig = KMDeckService.removeRow(updatedConfig);
      }
      setConfig(updatedConfig);
    }
  };

  const handleColumnsChange = (valueString: string, valueNumber: number) => {
    if (!config || isNaN(valueNumber) || valueNumber < 1 || valueNumber > 8) return;
    
    if (valueNumber > config.columns) {
      // Adding columns
      let updatedConfig = config;
      for (let i = config.columns; i < valueNumber; i++) {
        updatedConfig = KMDeckService.addColumn(updatedConfig);
      }
      setConfig(updatedConfig);
    } else if (valueNumber < config.columns) {
      // Removing columns
      let updatedConfig = config;
      for (let i = config.columns; i > valueNumber; i--) {
        updatedConfig = KMDeckService.removeColumn(updatedConfig);
      }
      setConfig(updatedConfig);
    }
  };

  // Touch event handlers for swipe navigation
  const handleTouchStart = (e: React.TouchEvent) => {
    if (!isMobileDevice() || !config || config.pages.length <= 1 || isTransitioning) return;
    setTouchStartX(e.targetTouches[0].clientX);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!isMobileDevice() || !config || config.pages.length <= 1 || isTransitioning) return;
    setTouchEndX(e.targetTouches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!isMobileDevice() || !config || config.pages.length <= 1 || isTransitioning) return;
    
    const swipeThreshold = 50; // Minimum distance for a swipe
    const swipeDistance = touchStartX - touchEndX;
    
    if (Math.abs(swipeDistance) > swipeThreshold) {
      setIsTransitioning(true);
      
      if (swipeDistance > 0) {
        // Swipe left - go to next main page
        const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
        const currentMainPageIndex = mainPages.findIndex(page => page.id === config.pages[currentPageIndex].id.replace('-landscape', ''));
        if (currentMainPageIndex < mainPages.length - 1) {
          const nextMainPage = mainPages[currentMainPageIndex + 1];
          const nextPageIndex = config.pages.findIndex(page => page.id === nextMainPage.id);
          setCurrentPageIndex(nextPageIndex);
        }
      } else {
        // Swipe right - go to previous main page
        const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
        const currentMainPageIndex = mainPages.findIndex(page => page.id === config.pages[currentPageIndex].id.replace('-landscape', ''));
        if (currentMainPageIndex > 0) {
          const prevMainPage = mainPages[currentMainPageIndex - 1];
          const prevPageIndex = config.pages.findIndex(page => page.id === prevMainPage.id);
          setCurrentPageIndex(prevPageIndex);
        }
      }
      
      // Reset transition state after animation
      setTimeout(() => {
        setIsTransitioning(false);
      }, 300);
    }
    
    // Reset touch positions
    setTouchStartX(0);
    setTouchEndX(0);
  };

  // Keyboard shortcuts handler
  const handleKeyDown = useCallback((e: KeyboardEvent) => {
    
    // Handle Escape key for setup modal
    if (e.key === 'Escape' && showSetup) {
      e.preventDefault();
      setShowSetup(false);
      return;
    }
    
    // Only handle other shortcuts when Cmd (Mac) or Ctrl (Windows/Linux) is pressed
    if (!e.metaKey && !e.ctrlKey) return;
    
    // Prevent default browser behavior for our shortcuts
    e.preventDefault();
    
    
    switch (e.key) {
      case '=':
        // Add page: ⌘=
        if (config && config.pages.length < 3) {
          const updatedConfig = KMDeckService.addPage(config);
          setConfig(updatedConfig);
          const mainPages = updatedConfig.pages.filter(page => !page.id.endsWith('-landscape'));
          const lastMainPage = mainPages[mainPages.length - 1];
          const newPageIndex = updatedConfig.pages.findIndex(page => page.id === lastMainPage.id);
          setCurrentPageIndex(newPageIndex);
        }
        break;
      case '-':
        // Remove page: ⌘-
        if (config && config.pages.length > 1) {
          const updatedConfig = KMDeckService.removePage(config, config.pages[currentPageIndex].id);
          setConfig(updatedConfig);
          setCurrentPageIndex(Math.min(currentPageIndex, updatedConfig.pages.length - 1));
        }
        break;
      case '.':
        // Navigate to next page: ⌘.
        const mainPages = config?.pages.filter(page => !page.id.endsWith('-landscape')) || [];
        const currentMainPageIndex = mainPages.findIndex(page => page.id === config?.pages[currentPageIndex].id.replace('-landscape', ''));
        if (currentMainPageIndex < mainPages.length - 1) {
          const nextMainPage = mainPages[currentMainPageIndex + 1];
          const nextPageIndex = config?.pages.findIndex(page => page.id === nextMainPage.id) || 0;
          setCurrentPageIndex(nextPageIndex);
        }
        break;
      case ',':
        // Navigate to previous page: ⌘,
        const mainPagesForPrev = config?.pages.filter(page => !page.id.endsWith('-landscape')) || [];
        const currentMainPageIndexForPrev = mainPagesForPrev.findIndex(page => page.id === config?.pages[currentPageIndex].id.replace('-landscape', ''));
        if (currentMainPageIndexForPrev > 0) {
          const prevMainPage = mainPagesForPrev[currentMainPageIndexForPrev - 1];
          const prevPageIndex = config?.pages.findIndex(page => page.id === prevMainPage.id) || 0;
          setCurrentPageIndex(prevPageIndex);
        }
        break;
      case 'ArrowRight':
        // Add column: ⌘→
        if (config && config.columns < 8) {
          const updatedConfig = KMDeckService.addColumn(config);
          setConfig(updatedConfig);
        }
        break;
      case 'ArrowLeft':
        // Remove column: ⌘←
        if (config && config.columns > 1) {
          const updatedConfig = KMDeckService.removeColumn(config);
          setConfig(updatedConfig);
        }
        break;
      case 'ArrowDown':
        // Add row: ⌘↓
        if (config && config.rows < 8) {
          const updatedConfig = KMDeckService.addRow(config);
          setConfig(updatedConfig);
        }
        break;
      case 'ArrowUp':
        // Remove row: ⌘↑
        if (config && config.rows > 1) {
          const updatedConfig = KMDeckService.removeRow(config);
          setConfig(updatedConfig);
        }
        break;
      case 'i':
        // Import: ⌘I
        fileInputRef.current?.click();
        break;
      case 'e':
        // Export: ⌘E
        if (config) {
          KMDeckService.exportConfig(config).catch(error => {
            if (error instanceof Error && error.message !== 'USER_CANCELLED') {
              console.error('[KMDeck] Failed to export config:', error);
            }
          });
        }
        break;
      case 'u':
        // Share: ⌘U
        onShareModalOpen();
        break;
      case 's':
        // Setup: ⌘S
        setShowSetup(true);
        break;
      case '/':
        // Toggle Portrait/Landscape: ⌘/
        setIsLandscapeMode(!isLandscapeMode);
        break;
    }
  }, [config, currentPageIndex, setCurrentPageIndex, onShareModalOpen, fileInputRef, showSetup, isLandscapeMode]);

  // Add keyboard event listener when config is available
  useEffect(() => {
    if (!config) return;
    
    // Add keyboard event listener for shortcuts
    window.addEventListener('keydown', handleKeyDown);
    
    return () => {
      // Remove keyboard event listener
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [config, handleKeyDown]);

  const getConfiguredButtonsCount = () => {
    if (!config) return 0;
    return config.pages.reduce((total, page) => 
      total + page.buttons.filter(button => {
        // Check regular buttons
        if (button.macroUuid && button.macroUuid.length > 0) {
          return true;
        }
        // Check toggle buttons - if either state has a macro configured
        if (button.isToggle && button.toggleState1 && button.toggleState2) {
          return (button.toggleState1.macroUuid && button.toggleState1.macroUuid.length > 0) ||
                 (button.toggleState2.macroUuid && button.toggleState2.macroUuid.length > 0);
        }
        return false;
      }).length, 0
    );
  };

  const canShare = () => {
    return config && getConfiguredButtonsCount() > 0;
  };

  const handleCopyShareLink = async () => {
    if (!config) return;
    
    try {
      const shareData = {
        config
      };
      
      const shareUrl = await KMDeckService.createShareUrlWithShortening(shareData);
      await navigator.clipboard.writeText(shareUrl);
      
      toast({
        title: 'Share link copied',
        description: 'The KMDeck link has been copied to your clipboard',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      console.error('[KMDeck] Failed to copy share link:', error);
      toast({
        title: 'Failed to copy link',
        description: 'Please try again',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const handlePasswordSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!password.trim()) {
      setPasswordError('Please enter a password');
      return;
    }

    setIsPasswordLoading(true);
    setPasswordError(null);

    try {
      const shareData = await KMDeckService.decryptShareDataFromUrl(password);
      if (shareData) {
        const configToUse = shareData.config;
        
        setRemoteTriggerUrl(configToUse.remoteTriggerUrl);
        
        // Load the favicon URL from shared config
        if (configToUse.faviconUrl) {
          setFaviconUrl(configToUse.faviconUrl);
        }
        
        setConfig(configToUse);
        setIsPasswordRequired(false);
        setPassword('');
        
        console.log('[KMDeck] Loaded password-protected configuration:', configToUse);
        
        // Clean the URL to hide share data parameters
        UrlCleaner.cleanUrl();
        
      } else {
        setPasswordError('Invalid password. Please try again.');
      }
    } catch (error) {
      console.error('[KMDeck] Failed to decrypt password-protected config:', error);
      setPasswordError('Failed to verify password. Please try again.');
    } finally {
      setIsPasswordLoading(false);
    }
  };

  const handlePasswordSetup = async (password: string): Promise<void> => {
    if (!config) return;
    
    try {
      const shareData = {
        config,
        isPasswordProtected: true
      };
      
      const shareUrl = await KMDeckService.createPasswordProtectedShareUrl(shareData, password);
      await navigator.clipboard.writeText(shareUrl);
      
      toast({
        title: 'Password protection enabled',
        description: 'Password-protected share link copied to clipboard',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      console.error('[KMDeck] Failed to create password-protected share link:', error);
      throw error;
    }
  };


  const handleExportConfig = async () => {
    if (!config) return;
    
    try {
      await KMDeckService.exportConfig(config);
    } catch (error) {
      // Don't show error toast if user cancelled
      if (error instanceof Error && error.message === 'USER_CANCELLED') {
        return;
      }
      console.error('[KMDeck] Failed to export config:', error);
      toast({
        title: 'Export failed',
        description: 'Failed to export configuration. Please try again.',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const handleImportConfig = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    await processImportedFile(file);
  };

  const processImportedFile = async (file: File) => {
    try {
      setIsLoading(true);
      const importedConfig = await KMDeckService.importConfig(file);
      
      setRemoteTriggerUrl(importedConfig.remoteTriggerUrl);
      
      // Update the configuration
      setConfig(importedConfig);
      
      // Update favicon URL if present
      if (importedConfig.faviconUrl) {
        setFaviconUrl(importedConfig.faviconUrl);
      }
      
      toast({
        title: 'Configuration imported',
        description: 'KMDeck configuration has been loaded successfully',
        status: 'success',
        duration: 3000,
        isClosable: true
      });
    } catch (error) {
      console.error('[KMDeck] Failed to import config:', error);
      toast({
        title: 'Import failed',
        description: 'Failed to import configuration file. Please check the file format.',
        status: 'error',
        duration: 5000,
        isClosable: true
      });
    } finally {
      setIsLoading(false);
      // Reset the file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  // Drag and drop handlers
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  };

  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);

    const files = Array.from(e.dataTransfer.files);
    const jsonFile = files.find(file => file.type === 'application/json' || file.name.endsWith('.json'));
    
    if (jsonFile) {
      await processImportedFile(jsonFile);
    } else {
      toast({
        title: 'Invalid file type',
        description: 'Please drop a JSON file to import a KMDeck configuration',
        status: 'error',
        duration: 3000,
        isClosable: true
      });
    }
  };

  const handleSplitPortraitLandscape = () => {
    onSplitModalOpen();
  };

  const handleUnsplitPage = () => {
    onUnsplitModalOpen();
  };

  const getLandscapePage = (portraitPage: any) => {
    if (!config || !portraitPage.isSplitPortraitLandscape) return portraitPage;
    
    // Get the landscape page for this portrait page (should already exist)
    const landscapePageId = `${portraitPage.id}-landscape`;
    const landscapePage = config.pages.find(p => p.id === landscapePageId);
    
    console.log('[KMDeck.getLandscapePage] Looking for landscape page:', {
      portraitPageId: portraitPage.id,
      landscapePageId,
      found: !!landscapePage,
      totalPages: config.pages.length,
      allPageIds: config.pages.map(p => p.id)
    });
    
    // If landscape page doesn't exist, return the portrait page as fallback
    return landscapePage || portraitPage;
  };

  const copyButtonsToLandscape = (portraitButtons: KMDeckButton[], landscapePageId: string) => {
    return portraitButtons.map(button => {
      // Extract the R1C1 position from the original button ID
      const parts = button.id.split('-');
      const r1c1Position = parts[parts.length - 1];
      
      // Create new button with landscape page ID
      return {
        ...button,
        id: `button-${landscapePageId}-${r1c1Position}`
      };
    });
  };

  const handleConfirmSplit = () => {
    console.log('[KMDeck.handleConfirmSplit] Starting split with config:', {
      totalPages: config?.pages.length,
      pageIds: config?.pages.map(p => p.id),
      currentPageIndex,
      resetLandscapeButtons
    });
    
    if (!config) return;

    const updatedConfig = { ...config };
    const currentPage = updatedConfig.pages[currentPageIndex];
    
    // Enable split mode for current page
    currentPage.isSplitPortraitLandscape = true;
    
    // Handle landscape page creation/removal
    const landscapePageId = `${currentPage.id}-landscape`;
    
    // Remove any existing landscape page first
    updatedConfig.pages = updatedConfig.pages.filter(page => page.id !== landscapePageId);
    
    if (resetLandscapeButtons) {
      // Create empty landscape page when reset is checked
      const landscapePage = {
        id: landscapePageId,
        name: `${currentPage.name} (Landscape)`,
        buttons: [] // Empty buttons array
      };
      
      // Insert landscape page right after its corresponding main page
      const mainPageIndex = updatedConfig.pages.findIndex(p => p.id === currentPage.id);
      updatedConfig.pages.splice(mainPageIndex + 1, 0, landscapePage);
      console.log('[KMDeck.handleConfirmSplit] Reset landscape buttons - created empty landscape page:', landscapePageId);
    } else {
      // Create landscape page with copied buttons when reset is not checked
      const landscapePage = {
        id: landscapePageId,
        name: `${currentPage.name} (Landscape)`,
        buttons: copyButtonsToLandscape(currentPage.buttons, landscapePageId)
      };
      
      // Insert landscape page right after its corresponding main page
      const mainPageIndex = updatedConfig.pages.findIndex(p => p.id === currentPage.id);
      updatedConfig.pages.splice(mainPageIndex + 1, 0, landscapePage);
      console.log('[KMDeck.handleConfirmSplit] Created landscape page with copied buttons:', landscapePageId);
    }

    console.log('[KMDeck.handleConfirmSplit] After split:', {
      totalPages: updatedConfig.pages.length,
      pageIds: updatedConfig.pages.map(p => p.id)
    });

    setConfig(updatedConfig);
    onSplitModalClose();
    setResetLandscapeButtons(false);
  };

  const handleConfirmUnsplit = () => {
    if (!config) return;

    const updatedConfig = { ...config };
    const currentPage = updatedConfig.pages[currentPageIndex];
    
    if (!currentPage.isSplitPortraitLandscape) {
      console.log('[KMDeck.handleConfirmUnsplit] Page is not split, nothing to do');
      onUnsplitModalClose();
      return;
    }

    const landscapePageId = `${currentPage.id}-landscape`;
    const landscapePage = updatedConfig.pages.find(p => p.id === landscapePageId);
    
    if (!landscapePage) {
      console.log('[KMDeck.handleConfirmUnsplit] No landscape page found');
      onUnsplitModalClose();
      return;
    }

    // Apply the selected view's settings to both views
    if (retainView === 'portrait') {
      // Keep portrait settings, remove landscape page
      updatedConfig.pages = updatedConfig.pages.filter(page => page.id !== landscapePageId);
      currentPage.isSplitPortraitLandscape = false;
      console.log('[KMDeck.handleConfirmUnsplit] Retained portrait settings, removed landscape page');
    } else {
      // Apply landscape settings to portrait page, then remove landscape page
      currentPage.buttons = [...landscapePage.buttons];
      currentPage.isSplitPortraitLandscape = false;
      updatedConfig.pages = updatedConfig.pages.filter(page => page.id !== landscapePageId);
      console.log('[KMDeck.handleConfirmUnsplit] Applied landscape settings to portrait page, removed landscape page');
    }

    setConfig(updatedConfig);
    onUnsplitModalClose();
    setRetainView('portrait'); // Reset to default
  };

  // Theme management functions
  const updateConfigTheme = (newTheme: KMDeckTheme) => {
    if (config) {
      const updatedConfig = { ...config, theme: newTheme };
      setConfig(updatedConfig);
    }
  };

  const previewTheme = (previewTheme: KMDeckTheme) => {
    if (config) {
      const updatedConfig = { ...config, theme: previewTheme };
      setConfig(updatedConfig);
    }
  };

  const restoreOriginalAppearance = () => {
    if (config) {
      const updatedConfig = { ...config };
      delete updatedConfig.theme;
      setConfig(updatedConfig);
    }
  };

  const testGlowEffect = () => {
    if (config && config.pages && config.pages.length > 0) {
      const currentPage = config.pages[currentPageIndex];
      if (currentPage) {
        // In landscape mode, use bottom-right button (last row, last column)
        // In portrait mode, use rightmost button in row 1
        const testPosition = isLandscapeMode 
          ? `R${config.rows}C${config.columns}` 
          : `R1C${config.columns}`;
        const testButtonId = `button-${currentPage.id}-${testPosition}`;
        triggerGlowEffect(testButtonId);
      }
    }
  };

  if (isLoading) {
    return (
      <Box minH="100vh" bg="#171923" color="gray.100">
        <Center h="100vh">
          <VStack spacing={4}>
            <Spinner size="xl" color="blue.500" />
            <Text color="gray.400">Loading KMDeck configuration...</Text>
          </VStack>
        </Center>
      </Box>
    );
  }

  return (
    <Box 
      minH="100vh" 
      h={isMobileDevice() ? "100vh" : "auto"}
      bg={config?.theme?.pageBackground || "#171923"} 
      color="gray.100"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      position="relative"
    >
      {/* Drag and drop overlay */}
      {isDragOver && (
        <Box
          position="fixed"
          top={0}
          left={0}
          right={0}
          bottom={0}
          bg="rgba(59, 130, 246, 0.1)"
          border="3px dashed"
          borderColor="blue.400"
          zIndex={9999}
          display="flex"
          alignItems="center"
          justifyContent="center"
          pointerEvents="none"
        >
          <VStack spacing={4} textAlign="center">
            <Box
              fontSize="4xl"
              color="blue.400"
            >
              📁
            </Box>
            <Text fontSize="xl" fontWeight="bold" color="blue.400">
              Drop JSON file to import KMDeck configuration
            </Text>
            <Text fontSize="md" color="blue.300">
              Release to load the configuration
            </Text>
          </VStack>
        </Box>
      )}

      <Container 
        maxW={isMobileDevice() ? "100%" : "container.lg"} 
        py={isMobileDevice() ? 0 : 4}
        h={isMobileDevice() ? "100%" : "auto"}
        px={isMobileDevice() ? 2 : 4}
      >
        {/* Header - Hidden on mobile and when password is required */}
        {!isMobileDevice() && !isPasswordRequired && (
          <Flex justify="center" align="center" mb={3} position="relative" mt="10px">
            <Button
              onClick={() => navigate('/')}
              variant="ghost"
              colorScheme="gray"
              size="sm"
              leftIcon={<ChevronLeftIcon />}
              position="absolute"
              left={0}
            >
              Back to ABmyMix
            </Button>

            {config && (
              <HStack spacing={2}>
                <Box
                  display="flex"
                  alignItems="center"
                  bg="gray.100"
                  borderRadius="md"
                  p={1}
                  border="1px solid"
                  borderColor="gray.300"
                  h="32px"
                  _dark={{
                    bg: "gray.700",
                    borderColor: "gray.600"
                  }}
                >
                  <Tooltip 
                    label={
                      <HStack spacing={2} color="gray.800">
                        <Text>Setup</Text>
                        <TooltipKeyBox>⌘S</TooltipKeyBox>
                      </HStack>
                    }
                  >
                    <Button
                      onClick={() => setShowSetup(true)}
                      size="sm"
                      colorScheme="blue"
                      variant="ghost"
                      borderRadius="sm"
                      flex="1"
                      mr={0.5}
                      data-testid="setup-button"
                      _hover={{
                        bg: "blue.100",
                        _dark: { bg: "blue.800" }
                      }}
                      _active={{
                        bg: "blue.200",
                        _dark: { bg: "blue.700" }
                      }}
                    >
                      Setup
                    </Button>
                  </Tooltip>
                  <Tooltip 
                    label={
                      <HStack spacing={2} color="gray.800">
                        <Text>Import Configuration</Text>
                        <TooltipKeyBox>⌘I</TooltipKeyBox>
                      </HStack>
                    }
                  >
                    <Button
                      onClick={handleImportConfig}
                      size="sm"
                      colorScheme="blue"
                      variant="ghost"
                      borderRadius="sm"
                      flex="1"
                      mx={0.5}
                      _hover={{
                        bg: "blue.100",
                        _dark: { bg: "blue.800" }
                      }}
                      _active={{
                        bg: "blue.200",
                        _dark: { bg: "blue.700" }
                      }}
                    >
                      Import
                    </Button>
                  </Tooltip>
                  {config && (
                    <Tooltip 
                      label={
                        <HStack spacing={2} color="gray.800">
                          <Text>Export Configuration</Text>
                          <TooltipKeyBox>⌘E</TooltipKeyBox>
                        </HStack>
                      }
                    >
                      <Button
                        onClick={handleExportConfig}
                        size="sm"
                        colorScheme="blue"
                        variant="ghost"
                        borderRadius="sm"
                        flex="1"
                        mx={0.5}
                        _hover={{
                          bg: "blue.100",
                          _dark: { bg: "blue.800" }
                        }}
                        _active={{
                          bg: "blue.200",
                          _dark: { bg: "blue.700" }
                        }}
                      >
                        Export
                      </Button>
                    </Tooltip>
                  )}
                  {config && (
                    <Tooltip 
                      label={
                        <HStack spacing={2} color="gray.800">
                          <Text>Share KMDeck</Text>
                          <TooltipKeyBox>⌘U</TooltipKeyBox>
                        </HStack>
                      }
                    >
                      <IconButton
                        aria-label="Share KMDeck"
                        icon={<LinkIcon />}
                        onClick={onShareModalOpen}
                        size="sm"
                        colorScheme="green"
                        variant="ghost"
                        borderRadius="sm"
                        _hover={{
                          bg: "green.100",
                          _dark: { bg: "green.800" }
                        }}
                        _active={{
                          bg: "green.200",
                          _dark: { bg: "green.700" }
                        }}
                      />
                    </Tooltip>
                  )}
                </Box>

                {/* Settings Gear Button */}
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Settings"
                    icon={<SettingsIcon />}
                    size="sm"
                    colorScheme="gray"
                    variant="ghost"
                    bg="gray.100"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.300"
                    h="32px"
                    _hover={{
                      bg: "gray.200",
                      _dark: { bg: "gray.600" }
                    }}
                    _active={{
                      bg: "gray.300",
                      _dark: { bg: "gray.500" }
                    }}
                    _dark={{
                      bg: "gray.700",
                      borderColor: "gray.600"
                    }}
                  />
                  <MenuList bg="gray.800" borderColor="gray.600">
                    <MenuItem
                      bg="gray.800"
                      color="gray.100"
                      _hover={{ bg: "gray.700" }}
                      _focus={{ bg: "gray.700" }}
                      onClick={onWhatsKMDeckOpen}
                    >
                      What&apos;s a KMDeck?
                    </MenuItem>
                    <MenuDivider borderColor="gray.600" />
                    <MenuItem
                      bg="gray.800"
                      color="gray.100"
                      _hover={{ bg: "gray.700" }}
                      _focus={{ bg: "gray.700" }}
                      onClick={() => setHideButtonLabels(!hideButtonLabels)}
                    >
                      {hideButtonLabels ? 'Show' : 'Hide'} Button Labels
                    </MenuItem>
                    <MenuItem
                      bg="gray.800"
                      color="gray.100"
                      _hover={{ bg: "gray.700" }}
                      _focus={{ bg: "gray.700" }}
                      onClick={onColorModalOpen}
                    >
                      Colors
                    </MenuItem>
                    {config && config.pages[currentPageIndex]?.isSplitPortraitLandscape ? (
                      <MenuItem
                        bg="gray.800"
                        color="gray.100"
                        _hover={{ bg: "gray.700" }}
                        _focus={{ bg: "gray.700" }}
                        onClick={handleUnsplitPage}
                      >
                        Unsplit page
                      </MenuItem>
                    ) : (
                      <MenuItem
                        bg="gray.800"
                        color="gray.100"
                        _hover={{ bg: "gray.700" }}
                        _focus={{ bg: "gray.700" }}
                        onClick={handleSplitPortraitLandscape}
                      >
                        Orientation Split
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              </HStack>
            )}
          </Flex>
        )}


        {/* Main Content - Always show the button grid */}

        {/* Password Entry Form */}
        {isPasswordRequired && (
          <Box
            position="fixed"
            top={0}
            left={0}
            right={0}
            bottom={0}
            bg="#171923"
            zIndex={10}
          >
            <Center h="100vh">
              <Box
                maxW="md"
                w="full"
                mx={4}
                bg="gray.800"
                borderRadius="lg"
                p={8}
                border="1px solid"
                borderColor="gray.600"
              >
              <VStack spacing={6} align="stretch">
                <Box textAlign="center">
                  <LockIcon boxSize={8} color="blue.400" mb={4} />
                  <Text fontSize="xl" fontWeight="bold" color="gray.100" mb={2}>
                    Password Required
                  </Text>
                  <Text color="gray.400" fontSize="sm">
                    This KMDeck is password protected. Please enter the password to continue.
                  </Text>
                </Box>

                {passwordError && (
                  <Alert status="error" borderRadius="md">
                    <AlertIcon />
                    <Text fontSize="sm">{passwordError}</Text>
                  </Alert>
                )}

                <form onSubmit={handlePasswordSubmit}>
                  <VStack spacing={4}>
                    <FormControl>
                      <InputGroup>
                        <Input
                          type={showPassword ? 'text' : 'password'}
                          placeholder="Enter password"
                          value={password}
                          onChange={(e) => setPassword(e.target.value)}
                          isDisabled={isPasswordLoading}
                          autoFocus
                          pr="4.5rem"
                          bg="gray.700"
                          borderColor="gray.600"
                          color="gray.100"
                          _focus={{
                            borderColor: "blue.400",
                            boxShadow: "0 0 0 1px #3182ce"
                          }}
                        />
                        <InputRightElement>
                          <IconButton
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                            icon={showPassword ? <ViewOffIcon /> : <ViewIcon />}
                            onClick={() => setShowPassword(!showPassword)}
                            variant="ghost"
                            size="sm"
                            isDisabled={isPasswordLoading}
                          />
                        </InputRightElement>
                      </InputGroup>
                    </FormControl>

                    <Button
                      type="submit"
                      colorScheme="blue"
                      width="full"
                      isLoading={isPasswordLoading}
                      loadingText="Verifying..."
                      isDisabled={!password.trim()}
                    >
                      {isPasswordLoading ? (
                        <Box display="flex" alignItems="center" gap={2}>
                          <Spinner size="sm" />
                          Verifying Password...
                        </Box>
                      ) : (
                        'Unlock KMDeck'
                      )}
                    </Button>
                  </VStack>
                </form>

                <Box
                  bg="yellow.900"
                  border="1px solid"
                  borderColor="yellow.700"
                  borderRadius="md"
                  p={3}
                >
                  <Text color="yellow.200" fontSize="xs">
                    <Text as="span" fontWeight="bold">Note:</Text> If you don&apos;t have the password, 
                    please contact the person who shared this KMDeck with you.
                  </Text>
                </Box>
              </VStack>
              </Box>
            </Center>
          </Box>
        )}

        {config && !isPasswordRequired && (
          <VStack 
            spacing={isMobileDevice() ? 0 : 6} 
            align="stretch"
            h={isMobileDevice() ? "100%" : "auto"}
            justify={isMobileDevice() ? "center" : "flex-start"}
            position="relative"
          >
            {/* Mobile Page Counter - Only show on mobile when multiple pages */}
            {isMobileDevice() && config.pages.length > 1 && (
              <Box
                className="kmdeck-page-counter"
                position="absolute"
                top="8px"
                left="8px"
                zIndex={10}
                bg="rgba(0, 0, 0, 0.7)"
                borderRadius="md"
                px={3}
                py={1}
                color="white"
                fontSize="sm"
                fontWeight="medium"
                pointerEvents="none"
              >
                {currentPageIndex + 1} / {config.pages.length}
              </Box>
            )}

            {/* Mobile Settings Button - Hidden on mobile as requested */}
            {/* {isMobileDevice() && config && mode === 'view' && !isPasswordRequired && (
              <Box
                position="absolute"
                top="8px"
                right="8px"
                zIndex={10}
              >
                <Menu>
                  <MenuButton
                    as={IconButton}
                    aria-label="Settings"
                    icon={<SettingsIcon />}
                    size="sm"
                    colorScheme="gray"
                    variant="ghost"
                    bg="rgba(0, 0, 0, 0.7)"
                    color="white"
                    borderRadius="md"
                    _hover={{
                      bg: "rgba(0, 0, 0, 0.8)"
                    }}
                    _active={{
                      bg: "rgba(0, 0, 0, 0.9)"
                    }}
                  />
                  <MenuList bg="gray.800" borderColor="gray.600">
                    <MenuItem
                      bg="gray.800"
                      color="gray.100"
                      _hover={{ bg: "gray.700" }}
                      _focus={{ bg: "gray.700" }}
                      onClick={() => setHideButtonLabels(!hideButtonLabels)}
                    >
                      {hideButtonLabels ? 'Show' : 'Hide'} Button Labels
                    </MenuItem>
                    <MenuItem
                      bg="gray.800"
                      color="gray.100"
                      _hover={{ bg: "gray.700" }}
                      _focus={{ bg: "gray.700" }}
                      onClick={onColorModalOpen}
                    >
                      Colors
                    </MenuItem>
                    {config && config.pages[currentPageIndex]?.isSplitPortraitLandscape ? (
                      <MenuItem
                        bg="gray.800"
                        color="gray.100"
                        _hover={{ bg: "gray.700" }}
                        _focus={{ bg: "gray.700" }}
                        onClick={handleUnsplitPage}
                      >
                        Unsplit page
                      </MenuItem>
                    ) : (
                      <MenuItem
                        bg="gray.800"
                        color="gray.100"
                        _hover={{ bg: "gray.700" }}
                        _focus={{ bg: "gray.700" }}
                        onClick={handleSplitPortraitLandscape}
                      >
                        Orientation Split
                      </MenuItem>
                    )}
                  </MenuList>
                </Menu>
              </Box>
            )} */}
            {/* Page Management - Hidden on mobile */}
            {!isMobileDevice() && (
              <Box textAlign="center">
                <HStack justify="center" spacing={4}>
                  {/* Page Management */}
                  <Tooltip 
                    label={
                      <VStack spacing={2} align="start" color="gray.800">
                        <HStack spacing={2}>
                          <Text>Previous Page</Text>
                          <TooltipKeyBox>⌘,</TooltipKeyBox>
                        </HStack>
                        <HStack spacing={2}>
                          <Text>Next Page</Text>
                          <TooltipKeyBox>⌘.</TooltipKeyBox>
                        </HStack>
                        <HStack spacing={2}>
                          <Text>Add Page</Text>
                          <TooltipKeyBox>⌘=</TooltipKeyBox>
                        </HStack>
                        <HStack spacing={2}>
                          <Text>Remove Page</Text>
                          <TooltipKeyBox>⌘-</TooltipKeyBox>
                        </HStack>
                      </VStack>
                    }
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      bg="gray.700"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.600"
                      overflow="hidden"
                      h="32px"
                      _hover={{
                        borderColor: "blue.400"
                      }}
                    >
                    {/* Previous Page Button - Only show when multiple pages */}
                    {config.pages.length > 1 && (
                      <Box
                          onClick={() => {
                            // Find the previous main page (skip landscape pages)
                            const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
                            const currentMainPageIndex = mainPages.findIndex(page => page.id === config.pages[currentPageIndex].id.replace('-landscape', ''));
                            if (currentMainPageIndex > 0) {
                              const prevMainPage = mainPages[currentMainPageIndex - 1];
                              const prevPageIndex = config.pages.findIndex(page => page.id === prevMainPage.id);
                              setCurrentPageIndex(prevPageIndex);
                            }
                          }}
                          cursor={(() => {
                            const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
                            const currentMainPageIndex = mainPages.findIndex(page => page.id === config.pages[currentPageIndex].id.replace('-landscape', ''));
                            return currentMainPageIndex === 0 ? "not-allowed" : "pointer";
                          })()}
                          opacity={(() => {
                            const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
                            const currentMainPageIndex = mainPages.findIndex(page => page.id === config.pages[currentPageIndex].id.replace('-landscape', ''));
                            return currentMainPageIndex === 0 ? 0.5 : 1;
                          })()}
                          px={1}
                          py={2}
                          color="gray.400"
                          _hover={{
                            bg: currentPageIndex === 0 ? undefined : "gray.600"
                          }}
                          _active={{
                            bg: currentPageIndex === 0 ? undefined : "gray.500"
                          }}
                          aria-label="Previous page"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <ChevronLeftIcon boxSize={5} />
                        </Box>
                    )}
                    
                    {/* Next Page Button - Only show when multiple pages */}
                    {config.pages.length > 1 && (
                      <Box
                          onClick={() => {
                            // Find the next main page (skip landscape pages)
                            const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
                            const currentMainPageIndex = mainPages.findIndex(page => page.id === config.pages[currentPageIndex].id.replace('-landscape', ''));
                            if (currentMainPageIndex < mainPages.length - 1) {
                              const nextMainPage = mainPages[currentMainPageIndex + 1];
                              const nextPageIndex = config.pages.findIndex(page => page.id === nextMainPage.id);
                              setCurrentPageIndex(nextPageIndex);
                            }
                          }}
                          cursor={(() => {
                            const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
                            const currentMainPageIndex = mainPages.findIndex(page => page.id === config.pages[currentPageIndex].id.replace('-landscape', ''));
                            return currentMainPageIndex === mainPages.length - 1 ? "not-allowed" : "pointer";
                          })()}
                          opacity={(() => {
                            const mainPages = config.pages.filter(page => !page.id.endsWith('-landscape'));
                            const currentMainPageIndex = mainPages.findIndex(page => page.id === config.pages[currentPageIndex].id.replace('-landscape', ''));
                            return currentMainPageIndex === mainPages.length - 1 ? 0.5 : 1;
                          })()}
                          px={1}
                          py={2}
                          color="gray.400"
                          _hover={{
                            bg: currentPageIndex === config.pages.length - 1 ? undefined : "gray.600"
                          }}
                          _active={{
                            bg: currentPageIndex === config.pages.length - 1 ? undefined : "gray.500"
                          }}
                          aria-label="Next page"
                          display="flex"
                          alignItems="center"
                          justifyContent="center"
                        >
                          <ChevronRightIcon boxSize={5} />
                        </Box>
                    )}
                    
                    {/* Separator */}
                    <Box
                      w="1px"
                      h="16px"
                      bg="gray.600"
                    />
                    
                    {/* Page Number */}
                    <Box
                      px={1}
                      py={2}
                      color="gray.200"
                      fontSize="sm"
                      fontWeight="medium"
                      minW="60px"
                      textAlign="center"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      gap={1}
                    >
                      Page {(() => {
                        const currentPage = config.pages[currentPageIndex];
                        const isLandscapePage = currentPage?.id.endsWith('-landscape');
                        
                        let pageNumber;
                        if (isLandscapePage) {
                          // For landscape pages, show the main page number
                          const mainPageId = currentPage.id.replace('-landscape', '');
                          const mainPageNumber = mainPageId.replace('page-', '');
                          pageNumber = mainPageNumber;
                        } else {
                          // For main pages, show the page number from the ID
                          const pageNumberFromId = currentPage?.id.replace('page-', '');
                          pageNumber = pageNumberFromId;
                        }
                        
                        console.log('[KMDeck] Displaying page number:', {
                          currentPageIndex,
                          pageNumber,
                          currentPageId: currentPage?.id,
                          isLandscapePage,
                          allPageIds: config.pages.map(p => p.id)
                        });
                        return pageNumber;
                      })()}
                      {config.pages[currentPageIndex].isSplitPortraitLandscape && (
                        <Box
                          as="span"
                          color="blue.400"
                          fontSize="xs"
                          title="This page has separate portrait/landscape configurations"
                        >
                          ⚡
                        </Box>
                      )}
                    </Box>
                    
                    {/* Separator */}
                    <Box
                      w="1px"
                      h="16px"
                      bg="gray.600"
                    />
                    
                    {/* Add Page Button */}
                    <Box
                        onClick={handleAddPage}
                        cursor={config.pages.length >= 3 ? "not-allowed" : "pointer"}
                        opacity={config.pages.length >= 3 ? 0.5 : 1}
                        px={2}
                        py={2}
                        color="green.400"
                        _hover={{
                          bg: config.pages.length >= 3 ? undefined : "green.50",
                          _dark: { bg: config.pages.length >= 3 ? undefined : "green.900" }
                        }}
                        _active={{
                          bg: config.pages.length >= 3 ? undefined : "green.100",
                          _dark: { bg: config.pages.length >= 3 ? undefined : "green.800" }
                        }}
                        aria-label="Add page"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <AddIcon boxSize={3} />
                      </Box>
                    
                    {/* Remove Page Button */}
                    <Box
                        onClick={handleRemovePage}
                        cursor={config.pages.length <= 1 ? "not-allowed" : "pointer"}
                        opacity={config.pages.length <= 1 ? 0.5 : 1}
                        px={2}
                        py={2}
                        color="red.400"
                        _hover={{
                          bg: config.pages.length <= 1 ? undefined : "red.50",
                          _dark: { bg: config.pages.length <= 1 ? undefined : "red.900" }
                        }}
                        _active={{
                          bg: config.pages.length <= 1 ? undefined : "red.100",
                          _dark: { bg: config.pages.length <= 1 ? undefined : "red.800" }
                        }}
                        aria-label="Remove page"
                        display="flex"
                        alignItems="center"
                        justifyContent="center"
                      >
                        <MinusIcon boxSize={3} />
                      </Box>
                    </Box>
                  </Tooltip>

                  {/* Grid Layout Controls */}
                  <Tooltip 
                    label={
                      <VStack spacing={2} align="start" color="gray.800">
                        <HStack spacing={2}>
                          <Text>Add Row</Text>
                          <TooltipKeyBox>⌘↓</TooltipKeyBox>
                        </HStack>
                        <HStack spacing={2}>
                          <Text>Remove Row</Text>
                          <TooltipKeyBox>⌘↑</TooltipKeyBox>
                        </HStack>
                        <HStack spacing={2}>
                          <Text>Add Column</Text>
                          <TooltipKeyBox>⌘→</TooltipKeyBox>
                        </HStack>
                        <HStack spacing={2}>
                          <Text>Remove Column</Text>
                          <TooltipKeyBox>⌘←</TooltipKeyBox>
                        </HStack>
                      </VStack>
                    }
                  >
                    <Box
                      display="flex"
                      alignItems="center"
                      bg="gray.700"
                      borderRadius="md"
                      border="1px solid"
                      borderColor="gray.600"
                      overflow="hidden"
                      h="32px"
                      _hover={{
                        borderColor: "blue.400"
                      }}
                    >
                    {/* Columns Control */}
                    <Box
                      px={2}
                      py={2}
                      color="gray.400"
                      fontSize="sm"
                      fontWeight="medium"
                      minW="20px"
                      textAlign="center"
                      mr={-5}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <RowsIcon size={16} color="#9CA3AF" />
                    </Box>
                    <NumberInput
                      value={config.columns}
                      onChange={handleColumnsChange}
                      min={1}
                      max={8}
                      size="sm"
                      w="60px"
                    >
                      <NumberInputField
                        bg="transparent"
                        border="none"
                        color="gray.100"
                        textAlign="center"
                        readOnly
                        cursor="default"
                        fontSize="sm"
                        _focus={{
                          borderColor: "transparent",
                          boxShadow: "none"
                        }}
                        _hover={{
                          bg: "gray.600"
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper 
                          color="gray.300" 
                          border="none"
                          bg="transparent"
                          _hover={{ bg: "gray.600" }}
                        >
                          <ChevronUpIcon boxSize={3} />
                        </NumberIncrementStepper>
                        <NumberDecrementStepper 
                          color="gray.300" 
                          border="none"
                          bg="transparent"
                          _hover={{ bg: "gray.600" }}
                        >
                          <ChevronDownIcon boxSize={3} />
                        </NumberDecrementStepper>
                      </NumberInputStepper>
                    </NumberInput>

                    {/* Separator */}
                    <Box
                      w="1px"
                      h="16px"
                      bg="gray.600"
                    />

                    {/* Rows Control */}
                    <Box
                      px={2}
                      py={2}
                      color="gray.400"
                      fontSize="sm"
                      fontWeight="medium"
                      minW="20px"
                      textAlign="center"
                      mr={-5}
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                    >
                      <ColumnsIcon size={16} color="#9CA3AF" />
                    </Box>
                    <NumberInput
                      value={config.rows}
                      onChange={handleRowsChange}
                      min={1}
                      max={8}
                      size="sm"
                      w="60px"
                    >
                      <NumberInputField
                        bg="transparent"
                        border="none"
                        color="gray.100"
                        textAlign="center"
                        readOnly
                        cursor="default"
                        fontSize="sm"
                        _focus={{
                          borderColor: "transparent",
                          boxShadow: "none"
                        }}
                        _hover={{
                          bg: "gray.600"
                        }}
                      />
                      <NumberInputStepper>
                        <NumberIncrementStepper 
                          color="gray.300" 
                          border="none"
                          bg="transparent"
                          _hover={{ bg: "gray.600" }}
                        >
                          <ChevronUpIcon boxSize={3} />
                        </NumberIncrementStepper>
                        <NumberDecrementStepper 
                          color="gray.300" 
                          border="none"
                          bg="transparent"
                          _hover={{ bg: "gray.600" }}
                        >
                          <ChevronDownIcon boxSize={3} />
                        </NumberDecrementStepper>
                      </NumberInputStepper>
                    </NumberInput>
                    </Box>
                  </Tooltip>
                </HStack>
              </Box>
            )}

            {/* Button Grid with Touch Support */}
            <Box
              className="kmdeck-page-transition"
              onTouchStart={handleTouchStart}
              onTouchMove={handleTouchMove}
              onTouchEnd={handleTouchEnd}
              style={{
                width: '100%',
                height: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                opacity: isTransitioning ? 0.8 : 1,
                transform: isTransitioning ? 'scale(0.98)' : 'scale(1)',
                transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)'
              }}
            >
              <ButtonGrid
                key={`${config.timestamp}-${currentPageIndex}-${isLandscapeMode}`}
                page={config.pages[currentPageIndex].isSplitPortraitLandscape && isLandscapeMode 
                  ? getLandscapePage(config.pages[currentPageIndex])
                  : config.pages[currentPageIndex]
                }
                rows={config.rows}
                columns={config.columns}
                onButtonClick={handleButtonClick}
                onButtonEdit={handleButtonEdit}
                onButtonSwap={handleButtonSwap}
                onButtonCopy={handleButtonCopy}
                availableHeight={availableHeight}
                isLandscape={isLandscapeMode}
                hideButtonLabels={hideButtonLabels}
                glowingButtons={glowingButtons}
                theme={config.theme}
              />
            </Box>

            {/* Orientation Toggle - Hidden on mobile */}
            {!isMobileDevice() && (
              <Box textAlign="center">
                <Tooltip 
                  label={
                    <Box>
                      Toggle Portrait/Landscape Mode
                      <TooltipKeyBox>⌘/</TooltipKeyBox>
                    </Box>
                  }
                  placement="top"
                  hasArrow
                >
                  <Box
                    display="inline-flex"
                    bg="gray.700"
                    borderRadius="md"
                    border="1px solid"
                    borderColor="gray.600"
                    overflow="hidden"
                    _hover={{
                      borderColor: "blue.400"
                    }}
                  >
                    <Button
                      onClick={() => setIsLandscapeMode(false)}
                      size="sm"
                      colorScheme={!isLandscapeMode ? "blue" : "gray"}
                      variant={!isLandscapeMode ? "solid" : "ghost"}
                      borderRadius="none"
                      borderRight="1px solid"
                      borderColor="gray.600"
                      _hover={{
                        bg: !isLandscapeMode ? "blue.600" : "gray.600"
                      }}
                      _active={{
                        bg: !isLandscapeMode ? "blue.700" : "gray.500"
                      }}
                      fontWeight="medium"
                      px={4}
                    >
                      Portrait
                    </Button>
                    <Button
                      onClick={() => setIsLandscapeMode(true)}
                      size="sm"
                      colorScheme={isLandscapeMode ? "blue" : "gray"}
                      variant={isLandscapeMode ? "solid" : "ghost"}
                      borderRadius="none"
                      _hover={{
                        bg: isLandscapeMode ? "blue.600" : "gray.600"
                      }}
                      _active={{
                        bg: isLandscapeMode ? "blue.700" : "gray.500"
                      }}
                      fontWeight="medium"
                      px={4}
                    >
                      Landscape
                    </Button>
                  </Box>
                </Tooltip>
              </Box>
            )}
          </VStack>
        )}

        {/* Modals */}
        <ButtonEditor
          isOpen={isButtonEditorOpen}
          onClose={onButtonEditorClose}
          onSave={handleButtonSave}
          button={editingButton}
          position={editingPosition}
          columns={config?.columns || 4}
          _remoteTriggerUrl={config?.remoteTriggerUrl || remoteTriggerUrl}
        />

        {/* Setup Modal */}
        <Modal isOpen={showSetup} onClose={() => setShowSetup(false)} size="lg" isCentered closeOnOverlayClick={false}>
          <ModalOverlay />
          <ModalContent bg="gray.800" border="1px solid" borderColor="gray.600">
            <ModalHeader color="gray.100">Setup Your KMDeck</ModalHeader>
            <ModalBody pb={6}>
              <VStack spacing={6} align="stretch">
                <Box
                  bg="blue.900"
                  borderRadius="md"
                  p={4}
                  border="1px solid"
                  borderColor="blue.700"
                >
                  <VStack spacing={2} align="stretch">
                    <Text color="blue.100" fontSize="sm">
                      1. Download and install the KMDeck companion macro from the link{' '}
                      <Text as="a" 
                        href="https://forum.keyboardmaestro.com/t/kmdeck-trigger-macros-from-any-device/50045" 
                        target="_blank" 
                        rel="noopener noreferrer"
                        color="blue.300"
                        textDecoration="underline"
                        _hover={{ color: "blue.200" }}
                        fontWeight="bold"
                      >
                        HERE
                      </Text>
                    </Text>
                    <Text color="blue.100" fontSize="sm">
                      2. Open the macro in Keyboard Maestro and copy the Remote Trigger Unique ID
                    </Text>
                    <Text color="blue.100" fontSize="sm">
                      3. Paste the Unique ID in the field below
                    </Text>
                  </VStack>
                </Box>
                
                <FormControl>
                  <FormLabel color="gray.200" fontSize="sm" fontWeight="medium">
                    Remote Trigger Unique ID
                  </FormLabel>
                  <Input
                    value={remoteTriggerUrl.startsWith('https://trigger.keyboardmaestro.com/t/') 
                      ? remoteTriggerUrl.replace('https://trigger.keyboardmaestro.com/t/', '')
                      : remoteTriggerUrl}
                    onChange={(e) => {
                      const inputValue = e.target.value;
                      // Always prepend the base URL in the background
                      const fullUrl = `https://trigger.keyboardmaestro.com/t/${inputValue}`;
                      setRemoteTriggerUrl(fullUrl);
                      updateConfigConnection(fullUrl);
                    }}
                    placeholder="E0437FD9-F103-4622-B64B-6F6D21B47883/5DDC5BF3-194B-437C-9C2F-A2736EAAD459"
                    bg="gray.700"
                    borderColor="gray.600"
                    color="gray.100"
                    fontSize="sm"
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px #3182ce"
                    }}
                  />
                  <Text color="gray.400" fontSize="xs" mt={1}>
                    Enter the Unique ID from your Keyboard Maestro macro&apos;s Remote Trigger settings
                  </Text>
                </FormControl>

                <FormControl>
                  {/* Icon Preview */}
                  <Box textAlign="center" mb={3}>
                    <Box
                      w="60px"
                      h="60px"
                      bg="gray.700"
                      borderRadius="lg"
                      border="2px solid"
                      borderColor="gray.600"
                      display="flex"
                      alignItems="center"
                      justifyContent="center"
                      mx="auto"
                      position="relative"
                      overflow="hidden"
                      style={{
                        backgroundImage: faviconUrl 
                          ? `url(${faviconUrl})` 
                          : `url(/kmdeck-favicon.png?v=1)`,
                        backgroundSize: 'cover',
                        backgroundPosition: 'center'
                      }}
                    >
                    </Box>
                  </Box>
                  
                  <Input
                    value={faviconUrl}
                    onChange={(e) => {
                      const newFaviconUrl = e.target.value;
                      setFaviconUrl(newFaviconUrl);
                      updateConfigFavicon(newFaviconUrl);
                    }}
                    placeholder="Home Screen Icon URL (Optional)"
                    bg="gray.700"
                    borderColor="gray.600"
                    color="gray.100"
                    fontSize="sm"
                    _focus={{
                      borderColor: "blue.400",
                      boxShadow: "0 0 0 1px #3182ce"
                    }}
                  />
                  <Text color="gray.400" fontSize="xs" mt={1}>
                    Right-click an image in your browser and Copy Image Address. Paste above.
                  </Text>
                </FormControl>

                <Button
                  onClick={() => {
                    // Check if the remote trigger URL contains the blocked ID
                    const inputValue = remoteTriggerUrl.startsWith('https://trigger.keyboardmaestro.com/t/') 
                      ? remoteTriggerUrl.replace('https://trigger.keyboardmaestro.com/t/', '')
                      : remoteTriggerUrl;
                    
                    if (inputValue === BLOCKED_REMOTE_TRIGGER_ID || inputValue.includes(BLOCKED_REMOTE_TRIGGER_ID)) {
                      onBlockedMacroAlertOpen();
                      return;
                    }
                    
                    if (KMDeckService.validateKeyboardMaestroUrl(remoteTriggerUrl)) {
                      setShowSetup(false);
                    } else {
                      toast({
                        title: 'Invalid URL',
                        description: 'Please enter a valid Keyboard Maestro remote trigger URL',
                        status: 'error',
                        duration: 3000,
                        isClosable: true
                      });
                    }
                  }}
                  size="md"
                  colorScheme="blue"
                  width="full"
                  isDisabled={!remoteTriggerUrl}
                >
                  Save Configuration
                </Button>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Hidden file input for import */}
        <input
          ref={fileInputRef}
          type="file"
          accept=".json"
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />

        {/* Share Modal */}
        <KMDeckShareModal
          isOpen={isShareModalOpen}
          onClose={onShareModalClose}
          config={config}
        />

        {/* Password Setup Modal */}
        <PasswordSetupModal
          isOpen={isPasswordSetupOpen}
          onClose={onPasswordSetupClose}
          onPasswordSet={handlePasswordSetup}
          onExport={handleExportConfig}
          title="Add Password Protection"
          showBackupWarning={true}
        />

        {/* Color Customization Modal */}
        <ColorCustomizationModal
          isOpen={isColorModalOpen}
          onClose={onColorModalClose}
          onSave={updateConfigTheme}
          onPreview={previewTheme}
          onRestoreOriginal={restoreOriginalAppearance}
          currentTheme={config?.theme}
        />

        {/* Split Portrait/Landscape Modal */}
        <Modal isOpen={isSplitModalOpen} onClose={onSplitModalClose} size="md" isCentered>
          <ModalOverlay />
          <ModalContent bg="gray.800" border="1px solid" borderColor="gray.600">
            <ModalHeader color="gray.100">Split Portrait/Landscape</ModalHeader>
            <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
                <Text color="gray.300" fontSize="sm">
                  This will allow you to configure buttons separately for portrait and landscape orientations on this page only. 
                  Other pages will continue to use the same buttons for both orientations.
                </Text>
                
                <FormControl>
                  <Checkbox
                    isChecked={resetLandscapeButtons}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => setResetLandscapeButtons(e.target.checked)}
                    colorScheme="blue"
                    color="gray.100"
                  >
                    Reset landscape buttons
                  </Checkbox>
                  <Text color="gray.400" fontSize="xs" mt={1}>
                    If unchecked, all current portrait button settings will be copied to landscape mode. 
                    If checked, landscape will start empty.
                  </Text>
                </FormControl>

                <HStack spacing={3} pt={2}>
                  <Button
                    onClick={onSplitModalClose}
                    variant="outline"
                    colorScheme="gray"
                    color="gray.300"
                    borderColor="gray.600"
                    _hover={{ bg: "gray.700" }}
                    flex={1}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmSplit}
                    colorScheme="blue"
                    bg="blue.500"
                    _hover={{ bg: "blue.600" }}
                    _active={{ bg: "blue.700" }}
                    flex={1}
                  >
                    Split
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Unsplit Portrait/Landscape Modal */}
        <Modal isOpen={isUnsplitModalOpen} onClose={onUnsplitModalClose} size="md" isCentered>
          <ModalOverlay />
          <ModalContent bg="gray.800" border="1px solid" borderColor="gray.600">
            <ModalHeader color="gray.100">Unsplit Page</ModalHeader>
            <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
                <Text color="gray.300" fontSize="sm">
                  This will merge the portrait and landscape views back into a single view. 
                  Choose which view's settings should be retained for both orientations.
                </Text>
                
                <FormControl>
                  <VStack spacing={3} align="stretch">
                    <Text color="gray.100" fontSize="sm" fontWeight="medium">
                      Retain settings from:
                    </Text>
                    <VStack spacing={2} align="stretch">
                      <Box
                        as="button"
                        onClick={() => setRetainView('portrait')}
                        p={3}
                        borderRadius="md"
                        border="2px solid"
                        borderColor={retainView === 'portrait' ? 'blue.500' : 'gray.600'}
                        bg={retainView === 'portrait' ? 'blue.50' : 'gray.700'}
                        _dark={{
                          bg: retainView === 'portrait' ? 'blue.900' : 'gray.700'
                        }}
                        _hover={{
                          borderColor: retainView === 'portrait' ? 'blue.500' : 'gray.500'
                        }}
                        transition="all 0.2s"
                        w="full"
                        textAlign="left"
                      >
                        <Text color="gray.100" fontWeight="medium">
                          Portrait View
                        </Text>
                        <Text color="gray.400" fontSize="xs">
                          Keep current portrait button settings
                        </Text>
                      </Box>
                      <Box
                        as="button"
                        onClick={() => setRetainView('landscape')}
                        p={3}
                        borderRadius="md"
                        border="2px solid"
                        borderColor={retainView === 'landscape' ? 'blue.500' : 'gray.600'}
                        bg={retainView === 'landscape' ? 'blue.50' : 'gray.700'}
                        _dark={{
                          bg: retainView === 'landscape' ? 'blue.900' : 'gray.700'
                        }}
                        _hover={{
                          borderColor: retainView === 'landscape' ? 'blue.500' : 'gray.500'
                        }}
                        transition="all 0.2s"
                        w="full"
                        textAlign="left"
                      >
                        <Text color="gray.100" fontWeight="medium">
                          Landscape View
                        </Text>
                        <Text color="gray.400" fontSize="xs">
                          Apply landscape button settings to both orientations
                        </Text>
                      </Box>
                    </VStack>
                  </VStack>
                </FormControl>

                <HStack spacing={3} pt={2}>
                  <Button
                    onClick={onUnsplitModalClose}
                    variant="outline"
                    colorScheme="gray"
                    color="gray.300"
                    borderColor="gray.600"
                    _hover={{ bg: "gray.700" }}
                    flex={1}
                  >
                    Cancel
                  </Button>
                  <Button
                    onClick={handleConfirmUnsplit}
                    colorScheme="blue"
                    bg="blue.500"
                    _hover={{ bg: "blue.600" }}
                    _active={{ bg: "blue.700" }}
                    flex={1}
                  >
                    Unsplit
                  </Button>
                </HStack>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* Blocked Macro Alert Modal */}
        <Modal isOpen={isBlockedMacroAlertOpen} onClose={onBlockedMacroAlertClose} size="md" isCentered>
          <ModalOverlay />
          <ModalContent bg="gray.800" border="1px solid" borderColor="gray.600">
            <ModalHeader color="gray.100">Security Update Required</ModalHeader>
            <ModalBody pb={6}>
              <VStack spacing={4} align="stretch">
                <Alert status="warning" borderRadius="md">
                  <AlertIcon />
                  <VStack spacing={3} align="stretch">
                    <Text color="gray.100" fontSize="sm" fontWeight="bold">
                      For security reasons, please do the following:
                    </Text>
                    <VStack spacing={2} align="stretch" pl={4}>
                      <Text color="gray.100" fontSize="sm">
                        • In Keyboard Maestro, remove the trigger from the KMDeck macro.
                      </Text>
                      <Text color="gray.100" fontSize="sm">
                        • Add a new Remote Trigger and copy the new unique ID (copy from the field; don't use the Copy button).
                      </Text>
                      <Text color="gray.100" fontSize="sm">
                        • Close this alert and paste the new Unique ID in the setup window.
                      </Text>
                    </VStack>
                    <Text color="gray.100" fontSize="sm" fontStyle="italic">
                      This is a one-time issue, and you won't see this alert again.
                    </Text>
                  </VStack>
                </Alert>

                <Button
                  onClick={() => {
                    onBlockedMacroAlertClose();
                    // Show setup window so user can enter new unique ID
                    setShowSetup(true);
                  }}
                  colorScheme="blue"
                  bg="blue.500"
                  _hover={{ bg: "blue.600" }}
                  _active={{ bg: "blue.700" }}
                  width="full"
                >
                  OK
                </Button>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

        {/* What's a KMDeck Modal */}
        <Modal isOpen={isWhatsKMDeckOpen} onClose={onWhatsKMDeckClose} size="md" isCentered>
          <ModalOverlay bg="blackAlpha.600" />
          <ModalContent bg="gray.800" maxW="md">
            <ModalHeader color="gray.100" borderBottom="1px solid" borderColor="gray.600">
              What&apos;s a KMDeck?
            </ModalHeader>
            <ModalCloseButton color="gray.300" />
            
            <ModalBody py={6}>
              <VStack spacing={4} align="stretch">
                <Text color="gray.200" fontSize="sm" lineHeight="1.6">
                  A KMDeck is a customizable button grid that lets you trigger Keyboard Maestro macros 
                  from any device on any network using remote triggers.
                </Text>
                

                <Box bg="#171923" borderRadius="md" p={4} border="1px solid" borderColor="gray.600">
                  <Text color="gray.300" fontSize="sm" fontWeight="medium" mb={2}>
                    How it works:
                  </Text>
                  <VStack spacing={1} align="stretch">
                    <Text color="gray.400" fontSize="xs">
                      1. Choose a button grid layout
                    </Text>
                    <Text color="gray.400" fontSize="xs">
                      2. Configure each button with a Keyboard Maestro macro UUID
                    </Text>
                    <Text color="gray.400" fontSize="xs">
                      3. Share the Deck to your mobile device 
                    </Text>
                  </VStack>
                </Box>

                <Box textAlign="center">
                  <Link
                    href="https://wiki.keyboardmaestro.com/trigger/Remote"
                    isExternal
                    color="blue.400"
                    fontSize="xs"
                  >
                    Learn about Remote Triggers <ExternalLinkIcon mx="2px" />
                  </Link>
                </Box>

                <Button
                  onClick={onWhatsKMDeckClose}
                  colorScheme="blue"
                  size="sm"
                  width="full"
                >
                  Got it
                </Button>
              </VStack>
            </ModalBody>
          </ModalContent>
        </Modal>

      </Container>
    </Box>
  );
};

export default KMDeck;
